// Generic function to sanitize strings (remove special chars, lowercase)
const sanitize = (str: string): string => {
  return str.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();
};

// Preserves case and dots — click IDs (gclid/fbclid/ttclid) are case-sensitive, and fbclid
// may carry the `fb.1.<clickTime>.<fbclid>` _fbc format. Use for click IDs, not affiliate codes.
const sanitizeClickId = (str: string): string => {
  return (str || '').replace(/[^A-Za-z0-9_.-]/g, '');
};

const UTM_COOKIE_MAX_AGE_SECONDS = 7 * 24 * 60 * 60;

const setCookie = (name: string, value: string, maxAgeSeconds: number) => {
  if (typeof window === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
};

const getCookie = (name: string): string | null => {
  if (typeof window === 'undefined') return null;
  const cookieValue = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];
  return cookieValue ? decodeURIComponent(cookieValue) : null;
};

// Function to read affiliate ID from cookie or URL
const readAffiliateId = () => {
  if (typeof window === 'undefined') return null;
  // Check cookie first
  const cookieValue = document.cookie
    .split('; ')
    .find((row) => row.startsWith('__dedf93rwdd2='))
    ?.split('=')[1];
  if (cookieValue) return cookieValue;
  // Fallback to URL
  const url = new URL(window.location.href);
  return url.searchParams.get('a_aid') || null;
};

// Function to store the latest click ID or affiliate ID in a single localStorage key (key=value format)
export const storeClickIds = () => {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const knownClickIds = {
    google: ['gclid', 'gbraid', 'wbraid'], // Google click IDs, prioritized
    facebook: ['fbclid'],
    tiktok: ['ttclid'],
  };

  let latestKeyValue: string | null = null;
  const now = Date.now();

  // Check for Google click IDs, prioritize gclid > gbraid > wbraid
  for (const param of knownClickIds.google) {
    const value = url.searchParams.get(param);
    if (value) {
      latestKeyValue = `${param}=${sanitizeClickId(value)}`;
      break; // Take the first (highest priority)
    }
  }

  // Check for Facebook
  if (!latestKeyValue) {
    const value = url.searchParams.get(knownClickIds.facebook[0]);
    if (value) {
      // Build the `_fbc`-format value (fb.1.<clickTimeMs>.<fbclid>) at capture time so
      // we have a correctly-formatted fbc even if the FB pixel hasn't set its own
      // `_fbc` cookie yet. `readClickIds` will prefer the pixel's `_fbc` cookie if
      // it shows up by the time we read this back (e.g. at checkout).
      latestKeyValue = `${knownClickIds.facebook[0]}=fb.1.${now}.${sanitizeClickId(value)}`;
    }
  }

  // Check for TikTok
  if (!latestKeyValue) {
    const value = url.searchParams.get(knownClickIds.tiktok[0]);
    if (value) {
      latestKeyValue = `${knownClickIds.tiktok[0]}=${sanitizeClickId(value)}`;
    }
  }

  // Check for Affiliate (only if no ad click ID)
  if (!latestKeyValue) {
    const affiliateId = readAffiliateId();
    if (affiliateId) {
      latestKeyValue = `affiliate=${sanitize(affiliateId)}`;
    }
  }

  if (latestKeyValue) {
    const data = {
      keyValue: latestKeyValue,
      expiry: now + 7 * 24 * 60 * 60 * 1000, // 7 days
    };
    localStorage.setItem('ads_clickid', JSON.stringify(data));
    document.cookie = `ads_clickid=${encodeURIComponent(latestKeyValue)}; Max-Age=${
      7 * 24 * 60 * 60
    }; Path=/; SameSite=Lax`;
  }
};

type UtmPayload = {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
};

const UTM_KEYS: Array<keyof UtmPayload> = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
];

const normalizeUtmValue = (value: string): string => value.trim().slice(0, 512);

export const storeUtmParams = () => {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const utmPayload = UTM_KEYS.reduce((acc, key) => {
    const value = url.searchParams.get(key);
    if (value) acc[key] = normalizeUtmValue(value);
    return acc;
  }, {} as Partial<UtmPayload>);

  if (Object.keys(utmPayload).length === 0) return;

  try {
    const existingRaw = localStorage.getItem('ads_utm');
    const existing = existingRaw ? (JSON.parse(existingRaw) as Partial<UtmPayload>) : {};
    const next = { ...existing, ...utmPayload };
    localStorage.setItem('ads_utm', JSON.stringify(next));
  } catch {
    // best effort; cookie mirror below still provides server visibility
  }

  UTM_KEYS.forEach((key) => {
    const val = utmPayload[key];
    if (val) {
      setCookie(`ads_${key}`, val, UTM_COOKIE_MAX_AGE_SECONDS);
    }
  });
};

export const readStoredUtmParams = (): Partial<UtmPayload> => {
  if (typeof window === 'undefined') return {};

  const fromCookies = UTM_KEYS.reduce((acc, key) => {
    const v = getCookie(`ads_${key}`);
    if (v) acc[key] = v;
    return acc;
  }, {} as Partial<UtmPayload>);

  try {
    const fromLsRaw = localStorage.getItem('ads_utm');
    const fromLs = fromLsRaw ? (JSON.parse(fromLsRaw) as Partial<UtmPayload>) : {};
    return { ...fromLs, ...fromCookies };
  } catch {
    return fromCookies;
  }
};

const readStoredClickIdValue = (): string | null => {
  if (typeof window === 'undefined') return null;

  const stored = localStorage.getItem('ads_clickid');
  if (!stored) {
    const cookieValue = document.cookie
      .split('; ')
      .find((row) => row.startsWith('ads_clickid='))
      ?.split('=')[1];
    return cookieValue ? decodeURIComponent(cookieValue) : null;
  }

  try {
    const data = JSON.parse(stored);
    if (Date.now() < data.expiry) {
      return data.keyValue;
    } else {
      localStorage.removeItem('ads_clickid'); // Expired, remove
      document.cookie = 'ads_clickid=; Max-Age=0; Path=/; SameSite=Lax';
      return null;
    }
  } catch (e) {
    localStorage.removeItem('ads_clickid');
    document.cookie = 'ads_clickid=; Max-Age=0; Path=/; SameSite=Lax';
  }

  const cookieValue = document.cookie
    .split('; ')
    .find((row) => row.startsWith('ads_clickid='))
    ?.split('=')[1];
  return cookieValue ? decodeURIComponent(cookieValue) : null;
};

// Function to read the stored click ID or affiliate ID (returns key=value string, or null)
export const readClickIds = () => {
  const keyValue = readStoredClickIdValue();
  if (!keyValue) return null;

  // Prefer the FB pixel's own `_fbc` cookie when available — it's the value FB itself
  // generated on click and matches the `fb.1.<clickTime>.<fbclid>` format CAPI expects
  // exactly. The pixel may not have set it yet at landing, but usually has by checkout.
  if (keyValue.startsWith('fbclid=')) {
    const fbc = getCookie('_fbc');
    if (fbc) return `fbclid=${sanitizeClickId(fbc)}`;
  }

  return keyValue;
};

const clearClickIdCookie = () => {
  if (typeof window === 'undefined') return;
  document.cookie = 'ads_clickid=; Max-Age=0; Path=/; SameSite=Lax';
};

const clearClickIdLocalStorage = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('ads_clickid');
};

export const clearStoredClickIds = () => {
  clearClickIdLocalStorage();
  clearClickIdCookie();
};

// Function to clear expired click ID or affiliate ID
export const clearExpiredClickIds = () => {
  if (typeof window === 'undefined') return;

  const stored = localStorage.getItem('ads_clickid');
  if (!stored) return;

  try {
    const data = JSON.parse(stored);
    if (Date.now() >= data.expiry) {
      clearStoredClickIds();
    }
  } catch (e) {
    clearStoredClickIds();
  }
};
